<template>
  <div class="w-full">
    <portal :to="type + '-stats-' + componentId">
      <div class="w-1/6 | table-left-cell">{{ typeNames[type] }}</div>
      <div class="w-1/6 | table-cell numeric text-right">
        {{ budgetRemainingPerDay.total | money }}
      </div>
      <div class="w-1/6 | table-cell numeric text-right">
        {{ budgetRemaining.total | money }}
      </div>
      <div
        class="w-1/6 | table-cell numeric text-right"
        :class="budgetHighlight(budgetPercentOverUnder.total)"
      >
        {{ budgetPercentOverUnder.total | percent }}
      </div>
      <div class="w-1/6 | table-cell empty text-center">-</div>
      <div class="w-1/6 | table-cell empty text-center">-</div>

      <slide-up-down
        class="w-full flex flex-wrap"
        :class="{ 'mb-4': expanded }"
        :active="expanded"
        :duration="500"
      >
        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-1'"
        >
          <div class="w-1/6 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right highlight">
            {{ budgetRemainingPerDay[service] | money }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right highlight">
            {{ budgetRemaining[service] | money }}
          </div>
          <div
            class="w-1/6 | table-cell numeric text-right highlight"
            :class="budgetHighlight(budgetPercentOverUnder[service])"
          >
            {{ budgetPercentOverUnder[service] | percent }}
          </div>
          <div class="w-1/6 | table-cell empty text-center">-</div>
          <div class="w-1/6 | table-cell empty text-center">-</div>
        </div>
      </slide-up-down>
    </portal>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-2': expanded }"
      :active="expanded"
      :duration="500"
    >
      <hr class="ruler" />
    </slide-up-down>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-4': expanded }"
      :active="expanded"
      :duration="500"
    >
      <div class="w-full text-center | heading">{{ typeNames[type] }}</div>
      <div class="w-full text-center border-r border-gray-800 | figure numeric">
        {{ (data[type].percent / 100) | percent }}
      </div>
      <div class="w-full text-center | label">Agency %</div>

      <div class="w-full flex flex-wrap mt-4">
        <div class="w-1/3">&nbsp;</div>
        <div class="w-1/3 | table-heading-t">Budget</div>
        <div class="w-1/3 | table-heading-t">Actual Spend</div>

        <div class="w-1/3 | table-left-cell indent">Total</div>
        <div class="w-1/3 | table-cell numeric text-right">
          {{ sum(data[type].budgets) | money }}
        </div>
        <div class="w-1/3 | table-cell numeric text-right">
          {{ actualBudget.total | money }}
        </div>

        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-2'"
        >
          <div class="w-1/3 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/3 | table-cell numeric text-right">
            {{ data[type].budgets[service] | money }}
          </div>
          <div class="w-1/3 | table-cell numeric text-right">
            {{ actualBudget[service] | money }}
          </div>
        </div>
      </div>
    </slide-up-down>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-4': expanded }"
      :active="expanded"
      :duration="500"
    >
      <div class="w-full flex flex-wrap mt-4">
        <div class="w-1/4">&nbsp;</div>
        <div class="w-1/4 | table-heading-t">Target Budget</div>
        <div class="w-1/4 | table-heading-t">.. To Date</div>
        <div class="w-1/4 | table-heading-t">Proj. Budget</div>

        <div class="w-1/4 | table-left-cell indent">Total</div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ targetBudget.total | money }}
        </div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ targetBudgetToDate.total | money }}
        </div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ projectedBudget.total | money }}
        </div>

        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-2'"
        >
          <div class="w-1/4 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ targetBudget[service] | money }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ targetBudgetToDate[service] | money }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ projectedBudget[service] | money }}
          </div>
        </div>
      </div>
    </slide-up-down>
  </div>
</template>

<script>
import HighlightMixin from "./HighlightMixin"
import { each } from "lodash"

export default {
  name: "BudgetAgencyItem",
  mixins: [HighlightMixin],
  props: {
    data: {
      required: true,
    },
    expanded: {
      required: true,
    },
    daysInPeriod: {
      required: true,
    },
    elapsedDaysInPeriod: {
      required: true,
    },
    componentId: {
      required: true,
    },
    type: {
      required: true,
    },
  },
  data() {
    return {
      higlights: {
        green: "green",
        yellow: "yellow",
        red: "red",
      },
      budgetKeys: {
        display: {
          google: "google_display",
          google_remarketing: "google_display_remarketing",
          facebook: "facebook_display",
          facebook_remarketing: "facebook_display_remarketing",
          simplifi: "simplifi_display",
        },
        video: {
          google: "google_video",
          google_remarketing: "google_video_remarketing",
          facebook: "facebook_video",
          facebook_remarketing: "facebook_video_remarketing",
          simplifi: "simplifi_tv",
          tremor: "tremor_tv",
        },
        sem: {
          google: "google_sem",
          bing: "bing_sem",
        },
      },
      serviceNames: {
        google_display: "Google",
        google_display_remarketing: "Google RM",
        facebook_display: "Facebook",
        facebook_display_remarketing: "Facebook RM",
        google_sem: "Google",
        google_video: "Google",
        google_video_remarketing: "Google RM",
        facebook_video: "Facebook",
        facebook_video_remarketing: "FB RM",
        simplifi_display: "Simpli.fi",
        simplifi_tv: "Simpli.fi",
        tremor_tv: "Tremor",
        bing_sem: "Bing",
      },
      typeNames: {
        display: "Display",
        video: "Video",
        sem: "SEM",
      },
    }
  },
  computed: {
    actualBudget() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] = this.data.stats[this.type][key].cost
      })

      data.total = this.sum(data)

      return data
    },
    targetBudget() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          this.data[this.type].budgets[item] /
          (1 + this.data[this.type].percent / 100)
      })

      data.total = this.sum(data)

      return data
    },
    projectedBudget() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          (this.data.stats[this.type][key].cost / this.elapsedDaysInPeriod) *
          this.daysInPeriod
      })

      data.total = this.sum(data)

      return data
    },
    targetBudgetToDate() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          (this.targetBudget[item] / this.daysInPeriod) *
          this.elapsedDaysInPeriod
      })

      data.total = this.sum(data)

      return data
    },
    budgetRemaining() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.targetBudget[item] - this.data.stats[this.type][key].cost
      })

      data.total = this.sum(data)

      return data
    },
    budgetRemainingPerDay() {
      let divisor = this.daysInPeriod - this.elapsedDaysInPeriod
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          divisor > 0
            ? this.budgetRemaining[item] / divisor
            : this.budgetRemaining[item]
      })

      data.total = this.sum(data)

      return data
    },
    budgetPercentOverUnder() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.targetBudgetToDate[item] > 0
            ? this.data.stats[this.type][key].cost /
              this.targetBudgetToDate[item]
            : 0
      })

      data.total =
        this.targetBudgetToDate.total > 0
          ? this.actualBudget.total / this.targetBudgetToDate.total
          : 0

      return data
    },
  },
  methods: {
    budgetHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 85,
            second: null,
            color: "green",
          },
          {
            key: 2,
            operator: "between",
            first: 85,
            second: 70,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 70,
            second: null,
            color: "red",
          },
        ],
        num
      )
    },
    sum(obj) {
      var sum = 0
      for (var el in obj) {
        if (obj.hasOwnProperty(el)) {
          sum += parseFloat(obj[el])
        }
      }
      return sum
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
